import { useMemo, useState } from 'react'
import { Input } from '../ui/Input'
import type { CategoryRow } from '../../services/categoryService'

type Props = {
  categories: CategoryRow[]
  value: string
  onChange: (categoryId: string) => void
  disabled?: boolean
}

export function HomepageSectionCategoryPicker({ categories, value, onChange, disabled }: Props) {
  const [query, setQuery] = useState('')

  const storeCategories = useMemo(
    () => categories.filter((c) => c.source === 'store' && c.id != null),
    [categories],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return storeCategories
    return storeCategories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q),
    )
  }, [storeCategories, query])

  const selected = storeCategories.find((c) => String(c.id) === value)

  return (
    <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
      <div>
        <p className="text-sm font-medium text-foreground">Category for this section</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Products from the chosen category appear on the homepage (up to 12 active items).
        </p>
      </div>

      {selected ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{selected.name}</p>
            <p className="truncate font-mono text-xs text-muted-foreground">{selected.slug}</p>
          </div>
          {!disabled ? (
            <button
              type="button"
              className="shrink-0 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => onChange('')}
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No category selected yet.</p>
      )}

      <Input
        label="Search categories"
        placeholder="Name or slug…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        autoComplete="off"
      />

      <ul
        className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-border bg-card p-1"
        role="listbox"
        aria-label="Categories"
      >
        {filtered.length === 0 ? (
          <li className="px-3 py-4 text-center text-sm text-muted-foreground">
            {storeCategories.length === 0 ? 'No store categories yet.' : 'No categories match your search.'}
          </li>
        ) : (
          filtered.map((c) => {
            const id = String(c.id)
            const isSelected = value === id
            return (
              <li key={id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={disabled}
                  onClick={() => onChange(id)}
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                    isSelected
                      ? 'bg-primary/15 font-medium text-foreground ring-1 ring-primary/30'
                      : 'hover:bg-muted/80'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <span className="min-w-0 truncate">{c.name}</span>
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">{c.slug}</span>
                </button>
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}
